import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

// Import services and hooks
import { auth, db } from './firebase/config';
import { verifyPaymobHmac } from './services/paymob';
import {
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect, 
    getRedirectResult,  
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    sendPasswordResetEmail,
    updateEmail,
    reauthenticateWithCredential,
    EmailAuthProvider,
    setPersistence, 
    browserLocalPersistence 
} from 'firebase/auth';
import {
    doc,
    getDoc,
    collection,
    onSnapshot,
    query,
    where,
    getDocs,
    writeBatch,
    deleteDoc,
    addDoc,
    updateDoc,
    serverTimestamp,
    setDoc,
    increment
} from 'firebase/firestore';
import emailjs from '@emailjs/browser';

// Import Layout and Global Components
import Layout from './components/Layout';
import Cart from './components/Cart';
import CheckoutModal from './components/CheckoutModal';
import AuthModal from './components/AuthModal';
import ImageLightbox from './components/ImageLightbox';
import Notification from './components/Notification';
import OrderConfirmationModal from './components/OrderConfirmationModal';
import AddProductModal from './components/AddProductModal';
import EditProductModal from './components/EditProductModal';
import ConfirmationModal from './components/ConfirmationModal';
import UploadingModal from './components/UploadingModal';
import MenuPanel from './components/MenuPanel';
import SearchOverlay from './components/SearchOverlay';
import TermsBanner from './components/TermsBanner';

// --- LAZY LOAD PAGES ---
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const ShopPage = React.lazy(() => import('./pages/ShopPage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const WishlistPage = React.lazy(() => import('./pages/WishlistPage'));
const OnSalePage = React.lazy(() => import('./pages/OnSalePage'));
const BundlesPage = React.lazy(() => import('./pages/BundlesPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const ReturnPolicyPage = React.lazy(() => import('./pages/ReturnPolicyPage'));

// Removed GEMINI_API_KEY

// EMAILJS KEYS
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID; 
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY; 

// ==========================================
// Feature 11: Meta Pixel Tracking
// Automatically tracks user events (PageView, AddToCart, InitiateCheckout, Purchase) for marketing analytics.
// ==========================================
// --- Meta Pixel Route Tracker ---
const RouteTracker = () => {
    const location = useLocation();
    useEffect(() => {
        if (window.fbq) {
            window.fbq('track', 'PageView');
        }
    }, [location]);
    return null;
};

// Simple Page Loader for Suspense
const PageLoader = () => (
    <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#b08d57] border-t-transparent rounded-full animate-spin"></div>
    </div>
);

export default function App() {
    // --- All State Management ---
    const [isAdmin, setIsAdmin] = useState(false);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '' });
    const [searchQuery, setSearchQuery] = useState('');
    const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [lightboxData, setLightboxData] = useState(null);
    const [orderConfirmation, setOrderConfirmation] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [couponDiscount, setCouponDiscount] = useState(0);
    const [couponError, setCouponError] = useState('');
    // Removed styling states
    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [announcement, setAnnouncement] = useState({ text: '', isActive: false, color: '#8B6939' });
    const [bundleSettings, setBundleSettings] = useState({ enabled: false, titleEn: 'Shop Bundles', titleAr: 'الباقات والعروض' });
    const [deliveryCosts, setDeliveryCosts] = useState({ alexandria: 50, cairo: 70, other: 100 });
    const [isFreeDelivery, setIsFreeDelivery] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isMenuPanelOpen, setIsMenuPanelOpen] = useState(false);
    const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
    
    const searchLogTimeout = useRef(null);

    // Sort products by position for display
    const sortedProducts = useMemo(() => {
        return [...products].sort((a, b) => (a.position || 0) - (b.position || 0));
    }, [products]);

    const showNotification = useCallback((message) => {
        setNotification({ show: true, message });
        setTimeout(() => setNotification({ show: false, message: '' }), 3000);
    }, []);

    // ==========================================
    // Scroll Lock & Mobile Back-Button Integration for Drawers/Modals
    // Ensures opening cart/menu/modals keeps exact scroll position and mobile back button closes drawer cleanly
    // ==========================================
    const savedScrollPosRef = useRef(0);
    const isLockedRef = useRef(false);
    const isOverlayOpen = Boolean(
        showCart ||
        isMenuPanelOpen ||
        isSearchOverlayOpen ||
        isCheckoutModalOpen ||
        isLoginModalOpen ||
        lightboxData
    );

    // 1. Lock body scroll safely while any overlay is open without losing the user's scroll position
    useEffect(() => {
        if (isOverlayOpen && !isLockedRef.current) {
            savedScrollPosRef.current = window.scrollY || document.documentElement.scrollTop || 0;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${savedScrollPosRef.current}px`;
            document.body.style.width = '100%';
            document.body.style.overflowY = 'scroll';
            isLockedRef.current = true;
        } else if (!isOverlayOpen && isLockedRef.current) {
            const restoreY = savedScrollPosRef.current;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflowY = '';
            isLockedRef.current = false;
            window.scrollTo({ top: restoreY, left: 0, behavior: 'instant' });
        }
    }, [isOverlayOpen]);

    // 2. Intercept mobile back button so pressing back closes the active drawer instead of leaving the page
    const activeOverlayRef = useRef(null);
    useEffect(() => {
        let current = null;
        if (showCart) current = 'cart';
        else if (isMenuPanelOpen) current = 'menu';
        else if (isSearchOverlayOpen) current = 'search';
        else if (isCheckoutModalOpen) current = 'checkout';
        else if (isLoginModalOpen) current = 'auth';
        else if (lightboxData) current = 'lightbox';

        if (current && !activeOverlayRef.current) {
            window.history.pushState({ ukiyoModal: current }, '');
            activeOverlayRef.current = current;
        } else if (!current && activeOverlayRef.current) {
            if (window.history.state?.ukiyoModal === activeOverlayRef.current) {
                window.history.back();
            }
            activeOverlayRef.current = null;
        } else {
            activeOverlayRef.current = current;
        }
    }, [showCart, isMenuPanelOpen, isSearchOverlayOpen, isCheckoutModalOpen, isLoginModalOpen, lightboxData]);

    useEffect(() => {
        const handlePopState = () => {
            if (activeOverlayRef.current) {
                const closing = activeOverlayRef.current;
                activeOverlayRef.current = null;
                if (closing === 'cart') setShowCart(false);
                else if (closing === 'menu') setIsMenuPanelOpen(false);
                else if (closing === 'search') setIsSearchOverlayOpen(false);
                else if (closing === 'checkout') setIsCheckoutModalOpen(false);
                else if (closing === 'auth') setIsLoginModalOpen(false);
                else if (closing === 'lightbox') setLightboxData(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);
    
    // ==========================================
    // Feature 1: Authentication & User Profiles
    // Manages user registration, login (Email & Google), password resets, profile updates, and session persistence.
    // ==========================================
    const mergeCarts = useCallback(async (user) => {
        if (!db) return;
        const localCartJson = localStorage.getItem('ukiyo-guest-cart');
        if (!localCartJson) return;
        const guestCart = JSON.parse(localCartJson);
        if (guestCart.length === 0) return;
        const batch = writeBatch(db);
        guestCart.forEach(item => {
            const docRef = doc(db, `users/${user.uid}/cart`, item.id);
            batch.set(docRef, item, { merge: true });
        });
        await batch.commit();
        localStorage.removeItem('ukiyo-guest-cart');
    }, []);

    const processLoginSuccess = useCallback(async (user, customName) => {
        if (!user) return;
        try {
            const userRef = doc(db, 'users', user.uid);
            const docSnap = await getDoc(userRef);
            const resolvedName = customName || user.displayName || user.email?.split('@')[0] || 'User';
            if (!docSnap.exists()) {
                await setDoc(userRef, { 
                    name: resolvedName, 
                    email: user.email,
                    createdAt: serverTimestamp() 
                });
            }
            await mergeCarts(user);
            setIsLoginModalOpen(false);
            showNotification(`Welcome back, ${resolvedName}!`);
        } catch (error) {
            console.error("Error processing login:", error);
        }
    }, [mergeCarts, showNotification]);

    // --- CONFIGURE PERSISTENCE ON MOUNT (Fixes Safari Popup Blocking) ---
    useEffect(() => {
        const enablePersistence = async () => {
            try {
                // Ensure persistence is set BEFORE any login attempt
                await setPersistence(auth, browserLocalPersistence);
            } catch (error) {
                console.error("Persistence Error:", error);
            }
        };
        enablePersistence();
    }, []);

    // --- CHECK REDIRECT RESULT (Fallback) ---
    useEffect(() => {
        getRedirectResult(auth)
            .then((result) => {
                if (result && result.user) {
                    processLoginSuccess(result.user);
                }
            })
            .catch((error) => {
                if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/popup-blocked') {
                    console.error("Redirect Login Error:", error);
                }
            });
    }, [processLoginSuccess]);

    // --- CHECK EMAIL SIGN-IN LINK ---
    useEffect(() => {
        if (isSignInWithEmailLink(auth, window.location.href)) {
            let email = window.localStorage.getItem('emailForSignIn');
            if (!email) {
                email = window.prompt('Please provide your email for confirmation');
            }
            if (email) {
                signInWithEmailLink(auth, email, window.location.href)
                    .then((result) => {
                        window.localStorage.removeItem('emailForSignIn');
                        window.history.replaceState({}, document.title, window.location.pathname);
                        if (result && result.user) {
                            processLoginSuccess(result.user);
                        }
                    })
                    .catch((error) => {
                        console.error("Email Link Login Error:", error);
                        showNotification(`Sign in failed: ${error.message}`);
                    });
            }
        }
    }, [processLoginSuccess, showNotification]);

    // --- OPTIMIZED GOOGLE SIGN IN (POPUP FIRST ON ALL DEVICES) ---
    // Modern mobile browsers (iOS Safari ITP & Android Chrome 3P cookie partitioning)
    // block cross-domain signInWithRedirect storage. Calling signInWithPopup directly
    // from a user tap works reliably across iPhone, Android, and desktop.
    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        
        try {
            const result = await signInWithPopup(auth, provider);
            if (result && result.user) {
                await processLoginSuccess(result.user);
            }
        } catch (error) {
            if (error.code === 'auth/popup-blocked') {
                try {
                    await signInWithRedirect(auth, provider);
                } catch (redirectError) {
                    console.error("Redirect Error:", redirectError);
                    showNotification("Login failed. Please check your browser settings.");
                }
            } else if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
                console.error("Google Sign In Error:", error);
                showNotification(`Sign in failed: ${error.message}`);
            }
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        setCart([]);
        showNotification("You have been logged out.");
    };

    // ... (All other handlers) ...

    // ==========================================
    // Feature 2: Shopping Cart & Checkout
    // Handles adding/removing items, guest cart merging upon login, calculating totals, and checkout flow.
    // ==========================================
    const handleAddToCart = useCallback((rawProduct) => {
        const normalizedPrice = Number(rawProduct.price) || 0;
        const rawSale = Number(rawProduct.salePrice);
        const product = {
            ...rawProduct,
            price: normalizedPrice,
            salePrice: (rawSale > 0 && rawSale < normalizedPrice) ? rawSale : null
        };
        const availableStock = typeof product.quantity === 'number' ? product.quantity : Infinity;
        if (availableStock <= 0) {
            showNotification(`${product.name} is out of stock.`);
            return;
        }

        const existingCartItem = cart.find(item => item.id === product.id);
        const currentQtyInCart = existingCartItem ? existingCartItem.quantity : 0;
        if (currentQtyInCart + 1 > availableStock) {
            showNotification(`Only ${availableStock} available in stock.`);
            return;
        }

        // Meta Pixel Tracking
        if (window.fbq) {
            window.fbq('track', 'AddToCart', {
                content_ids: [product.id],
                content_name: product.name,
                content_type: 'product',
                value: product.salePrice || product.price,
                currency: 'EGP'
            });
        }

        if (!currentUser) {
            const newCart = [...cart];
            const existingItemIndex = newCart.findIndex(item => item.id === product.id);
            if (existingItemIndex > -1) {
                newCart[existingItemIndex].quantity += 1;
            } else {
                newCart.push({ ...product, quantity: 1 });
            }
            setCart(newCart);
            localStorage.setItem('ukiyo-guest-cart', JSON.stringify(newCart));
        } else {
            const cartItemRef = doc(db, `users/${currentUser.uid}/cart`, product.id);
            getDoc(cartItemRef).then(docSnap => {
                const newQuantity = docSnap.exists() ? docSnap.data().quantity + 1 : 1;
                if (newQuantity > availableStock) {
                    showNotification(`Only ${availableStock} available in stock.`);
                    return;
                }
                setDoc(cartItemRef, { ...product, quantity: newQuantity }, { merge: true });
            });
        }
        showNotification(`${product.name} added to cart!`);
    }, [currentUser, cart, showNotification]);

    const handleAddBundleToCart = useCallback(async (bundle, includedProducts) => {
        if (!includedProducts || includedProducts.length === 0) return;

        for (const p of includedProducts) {
            const storeProduct = products.find(sp => sp.id === p.id) || p;
            const currentInCart = cart.find(ci => ci.id === p.id)?.quantity || 0;
            const availableStock = typeof storeProduct.quantity === 'number' ? storeProduct.quantity : Infinity;
            if (currentInCart + 1 > availableStock) {
                showNotification(`"${p.name}" in this bundle exceeds available stock.`);
                return;
            }
        }

        const totalOrig = includedProducts.reduce((sum, p) => sum + Number(p.salePrice || p.price || 0), 0);
        const targetBundlePrice = Number(bundle.bundlePrice) || totalOrig;
        const discountRatio = totalOrig > 0 ? (targetBundlePrice / totalOrig) : 1;

        let accumulated = 0;
        const itemsToAdd = includedProducts.map((p, idx) => {
            const orig = Number(p.salePrice || p.price || 0);
            let itemPrice;
            if (idx === includedProducts.length - 1) {
                itemPrice = Math.max(0, targetBundlePrice - accumulated);
            } else {
                itemPrice = Math.round(orig * discountRatio);
                accumulated += itemPrice;
            }

            return {
                ...p,
                price: Number(p.price) || itemPrice,
                salePrice: itemPrice,
                bundleId: bundle.id,
                bundleName: bundle.name
            };
        });

        if (!currentUser) {
            let newCart = [...cart];
            itemsToAdd.forEach(item => {
                const existingIndex = newCart.findIndex(ci => ci.id === item.id);
                if (existingIndex > -1) {
                    newCart[existingIndex].quantity += 1;
                    newCart[existingIndex].salePrice = item.salePrice;
                } else {
                    newCart.push({ ...item, quantity: 1 });
                }
            });
            setCart(newCart);
            localStorage.setItem('ukiyo-guest-cart', JSON.stringify(newCart));
        } else {
            for (const item of itemsToAdd) {
                const cartItemRef = doc(db, `users/${currentUser.uid}/cart`, item.id);
                const docSnap = await getDoc(cartItemRef);
                const newQuantity = docSnap.exists() ? docSnap.data().quantity + 1 : 1;
                await setDoc(cartItemRef, { ...item, quantity: newQuantity }, { merge: true });
            }
        }

        if (window.fbq) {
            window.fbq('track', 'AddToCart', {
                content_ids: itemsToAdd.map(i => i.id),
                content_name: bundle.name,
                content_type: 'product_group',
                value: targetBundlePrice,
                currency: 'EGP'
            });
        }

        setShowCart(true);
        showNotification(`${bundle.name} (${itemsToAdd.length} items) added to cart!`);
    }, [currentUser, cart, products, showNotification]);

    const handleRemoveFromCart = useCallback((productId) => {
        if (currentUser) {
            deleteDoc(doc(db, `users/${currentUser.uid}/cart`, productId));
        } else {
            const newCart = cart.filter(item => item.id !== productId);
            setCart(newCart);
            localStorage.setItem('ukiyo-guest-cart', JSON.stringify(newCart));
        }
    }, [currentUser, cart]);

    const handleUpdateCartItem = useCallback((productId, newQuantity) => {
        if (newQuantity < 1) {
            handleRemoveFromCart(productId);
            return;
        }
        const storeProduct = products.find(p => p.id === productId);
        if (storeProduct && typeof storeProduct.quantity === 'number' && newQuantity > storeProduct.quantity) {
            showNotification(`Only ${storeProduct.quantity} available in stock.`);
            return;
        }
        if (currentUser) {
            updateDoc(doc(db, `users/${currentUser.uid}/cart`, productId), { quantity: newQuantity });
        } else {
            const newCart = cart.map(item => item.id === productId ? { ...item, quantity: newQuantity } : item);
            setCart(newCart);
            localStorage.setItem('ukiyo-guest-cart', JSON.stringify(newCart));
        }
    }, [currentUser, cart, products, handleRemoveFromCart, showNotification]);
    
    // ==========================================
    // Feature 5: Wishlist System
    // Lets authenticated users save favorite products to a personalized wishlist.
    // ==========================================
    const handleToggleWishlist = async (product, isWishlisted) => {
        if (!currentUser) {
            showNotification("Please log in to use the wishlist.");
            setIsLoginModalOpen(true);
            return;
        }
        const wishlistRef = doc(db, `users/${currentUser.uid}/wishlist`, product.id);
        if (isWishlisted) {
            await deleteDoc(wishlistRef);
            showNotification(`${product.name} removed from wishlist.`);
        } else {
            await setDoc(wishlistRef, { productId: product.id, createdAt: serverTimestamp() });
            
            // Meta Pixel AddToWishlist Tracking
            if (window.fbq) {
                window.fbq('track', 'AddToWishlist', {
                    content_name: product.name,
                    content_category: product.category,
                    content_ids: [product.id],
                    content_type: 'product',
                    value: product.salePrice || product.price,
                    currency: 'EGP'
                });
            }

            showNotification(`${product.name} added to wishlist!`);
        }
    };

    // ==========================================
    // Feature 1 (Continued): Authentication & User Profiles
    // ==========================================
    const handleEmailRegister = async (name, email, password) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            processLoginSuccess(userCredential.user, name);
        } catch (error) {
            showNotification(`Registration failed: ${error.message}`);
        }
    };

    const handleEmailLogin = async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            setIsLoginModalOpen(false);
            showNotification("Logged in successfully!");
        } catch (error) {
            showNotification(`Login failed: ${error.message}`);
        }
    };

    const handleSendSignInLink = async (email) => {
        const actionCodeSettings = { url: window.location.origin, handleCodeInApp: true };
        try {
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            window.localStorage.setItem('emailForSignIn', email);
            showNotification("Sign-in link sent!");
        } catch (error) {
            showNotification(`Error sending link: ${error.message}`);
        }
    };

    const handleUpdateUserInfo = async (newInfo) => {
        if (!currentUser) return;
        const userRef = doc(db, "users", currentUser.uid);
        try {
            await updateDoc(userRef, newInfo);
            showNotification("Profile updated successfully!");
        } catch (error) {
            showNotification("Failed to update profile.");
        }
    };

    const handlePasswordReset = async () => {
        if (!currentUser?.email) return;
        try {
            await sendPasswordResetEmail(auth, currentUser.email);
            showNotification("Password reset email sent. Check your inbox.");
        } catch (error) {
            showNotification("Failed to send password reset email.");
        }
    };

    const handleEmailUpdate = async (newEmail, password) => {
        if (!currentUser) return;
        try {
            const credential = EmailAuthProvider.credential(currentUser.email, password);
            await reauthenticateWithCredential(currentUser, credential);
            await updateEmail(currentUser, newEmail);
            const userRef = doc(db, "users", currentUser.uid);
            await updateDoc(userRef, { email: newEmail });
            showNotification("Email updated successfully. Please log in again.");
            handleLogout();
        } catch (error) {
            showNotification(`Error updating email: ${error.message}`);
        }
    };

    // ==========================================
    // Feature 3: Product Management
    // Allows admins to add, edit, delete, reorder, and bulk-update products (including CSV imports).
    // ==========================================
    const handleAddProduct = async (productData) => {
        const { id, ...data } = productData;
        if (!id || !data.name) {
            showNotification("Product ID and Name are required.");
            return;
        }
        const newPosition = products.length > 0 ? Math.max(...products.map(p => p.position || 0)) + 1 : 0;
        try {
            setIsUploading(true);
            await setDoc(doc(db, "products", id), { 
                ...data, 
                position: newPosition,
                reviewCount: 0,
                averageRating: 0,
                createdAt: serverTimestamp() 
            });
            showNotification("Product added successfully!");
            setIsAddProductModalOpen(false);
        } catch (error) {
            showNotification("Failed to add product.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleUpdateProduct = async (updatedData) => {
        const { id, ...data } = updatedData;
        try {
            setIsUploading(true);
            await updateDoc(doc(db, "products", id), data);
            showNotification("Product updated successfully!");
            setEditingProduct(null);
        } catch (error) {
            showNotification("Failed to update product.");
        } finally {
            setIsUploading(false);
        }
    };

    const confirmDeleteProduct = async (productIds) => {
        if (!productIds || productIds.length === 0) return;
        
        const productOrProducts = productIds.length > 1 ? "products" : "product";
        if (!window.confirm(`Are you sure you want to permanently delete ${productIds.length} ${productOrProducts}? This cannot be undone.`)) {
            setIsConfirmModalOpen(false);
            setProductToDelete(null);
            return;
        }

        try {
            setIsUploading(true);
            const batch = writeBatch(db);
            productIds.forEach(id => {
                const docRef = doc(db, "products", id);
                batch.delete(docRef);
            });
            await batch.commit();
            showNotification(`${productIds.length} ${productOrProducts} deleted successfully!`);
        } catch (error) {
            showNotification("Failed to delete products.");
        } finally {
            setIsUploading(false);
            setIsConfirmModalOpen(false);
            setProductToDelete(null);
        }
    };

    // ==========================================
    // Feature 6: Coupons & Discounts
    // Admin-managed promotional codes (fixed, percentage, free delivery) with usage conditions, applied at checkout.
    // ==========================================
    const handleApplyCoupon = async (code) => {
        if (!code.trim()) {
            setCouponError("Please enter a coupon code.");
            return;
        }
        setCouponError('');
        setIsFreeDelivery(false);
        setCouponDiscount(0);

        const couponRef = doc(db, "coupons", code.toUpperCase());
        try {
            const docSnap = await getDoc(couponRef);
            if (!docSnap.exists()) {
                setCouponError("Invalid coupon code.");
                return;
            }

            const coupon = docSnap.data();
            const subtotal = cart.reduce((sum, item) => sum + (item.salePrice || item.price) * item.quantity, 0);

            if (coupon.expiryDate && new Date(coupon.expiryDate.seconds * 1000) < new Date()) {
                setCouponError("This coupon has expired.");
                return;
            }
            if (coupon.minPurchase && subtotal < coupon.minPurchase) {
                setCouponError(`This coupon requires a minimum purchase of EGP ${coupon.minPurchase}.`);
                return;
            }

            if (currentUser) {
                const ordersRef = collection(db, "orders");
                if (coupon.firstOrderOnly) {
                    const q = query(ordersRef, where("userId", "==", currentUser.uid));
                    const userOrdersSnap = await getDocs(q);
                    if (!userOrdersSnap.empty) {
                        setCouponError("This coupon is for first-time customers only.");
                        return;
                    }
                }
                if (coupon.usageLimit) {
                    const q = query(ordersRef, where("userId", "==", currentUser.uid), where("coupon", "==", code.toUpperCase()));
                    const usedOrdersSnap = await getDocs(q);
                    if (usedOrdersSnap.size >= coupon.usageLimit) {
                        setCouponError("You have reached the usage limit for this coupon.");
                        return;
                    }
                }
            }

            if (coupon.type === 'free_delivery') {
                setIsFreeDelivery(true);
                showNotification("Free delivery unlocked!");
                return;
            }

            let discount = 0;
            if (coupon.type === 'percentage') {
                discount = (subtotal * coupon.value) / 100;
                if (coupon.maxDiscount && discount > coupon.maxDiscount) {
                    discount = coupon.maxDiscount;
                }
            } else if (coupon.type === 'fixed') {
                discount = coupon.value;
            }
            
            setCouponDiscount(discount);
            showNotification("Coupon applied successfully!");

        } catch (error) {
            console.error("Error applying coupon:", error);
            setCouponError("Could not apply coupon.");
            showNotification("An error occurred while applying the coupon.");
        }
    };

    // ==========================================
    // Feature 7: Search & Analytics
    // Provides a product search overlay and logs search terms/results for the admin analytics dashboard.
    // ==========================================
    const handleLogSearch = useCallback((term, foundResults) => {
        if (!term || term.length < 3) return;
        if (searchLogTimeout.current) {
            clearTimeout(searchLogTimeout.current);
        }
        searchLogTimeout.current = setTimeout(async () => {
            const searchTerm = term.toLowerCase().trim();
            const searchRef = doc(db, "searchAnalytics", searchTerm);
            try {
                await setDoc(searchRef, {
                    term: searchTerm,
                    count: increment(1),
                    lastSearched: serverTimestamp(),
                    foundResults: foundResults
                }, { merge: true });
            } catch (error) {
                console.error("Error logging search:", error);
            }
        }, 1000);
    }, []);

    // ==========================================
    // Feature 2 (Continued): Shopping Cart & Checkout
    // ==========================================
    const handleOpenCheckout = () => {
        setShowCart(false);
        setIsCheckoutModalOpen(true);
        
        // Meta Pixel Tracking
        if (window.fbq) {
            const checkoutTotal = cart.reduce((sum, item) => sum + Number(item.salePrice || item.price) * item.quantity, 0);
            window.fbq('track', 'InitiateCheckout', {
                value: checkoutTotal,
                currency: 'EGP',
                content_ids: cart.map(item => item.id),
                num_items: cart.length
            });
        }
    };

    // ==========================================
    // Feature 4: Order Processing & Tracking
    // Saves user/guest orders, sends Telegram/Email notifications, and lets admins update order statuses.
    // ==========================================
    const [authReady, setAuthReady] = useState(false);

    const placeOrder = async (orderDetails) => {
        const activeUser = currentUser || auth.currentUser;
        const resolvedUserId = activeUser?.uid || (orderDetails.userId && orderDetails.userId !== 'guest' ? orderDetails.userId : 'guest');
        const resolvedEmail = orderDetails?.customer?.email || activeUser?.email || '';

        if (resolvedUserId === 'guest' && !resolvedEmail) {
            showNotification("Please provide your email to place a guest order.");
            return;
        }

        try {
            setIsUploading(true);
            const cleanOrderDetails = JSON.parse(JSON.stringify(orderDetails));

            const orderData = {
                ...cleanOrderDetails,
                userId: resolvedUserId,
                guestEmail: resolvedEmail,
                createdAt: serverTimestamp(),
                status: 'Placed'
            };
            
            // 1. Save to Firestore
            const orderRef = await addDoc(collection(db, "orders"), orderData);
            const orderId = orderRef.id;

            // 2. Clear Cart Immediately (Local State, LocalStorage, and Firestore User Cart)
            setCart([]);
            localStorage.removeItem('ukiyo-guest-cart');
            setCouponDiscount(0);
            setIsFreeDelivery(false);
            setCouponError('');

            if (resolvedUserId && resolvedUserId !== 'guest') {
                try {
                    const cartRef = collection(db, `users/${resolvedUserId}/cart`);
                    const cartSnapshot = await getDocs(cartRef);
                    const batch = writeBatch(db);
                    cartSnapshot.forEach(cartDoc => {
                        batch.delete(cartDoc.ref);
                    });
                    if (Array.isArray(orderDetails.items)) {
                        orderDetails.items.forEach(item => {
                            if (item?.id) {
                                batch.delete(doc(db, `users/${resolvedUserId}/cart`, String(item.id)));
                            }
                        });
                    }
                    await batch.commit();
                } catch (cartClearErr) {
                    console.warn("Error clearing Firestore user cart:", cartClearErr);
                }
            }

            // 2b. Decrement product stock quantities in Firestore
            if (Array.isArray(orderDetails.items)) {
                await Promise.all(
                    orderDetails.items.map(async (item) => {
                        const storeProduct = products.find(p => p.id === item.id);
                        if (storeProduct && typeof storeProduct.quantity === 'number') {
                            const updatedQty = Math.max(0, storeProduct.quantity - (item.quantity || 1));
                            try {
                                await updateDoc(doc(db, "products", item.id), { quantity: updatedQty });
                            } catch (stockErr) {
                                console.warn(`Could not decrement stock for product ${item.id}:`, stockErr);
                            }
                        }
                    })
                );
            }

            // Meta Pixel Purchase Tracking (Moved here for Deduplication)
            if (window.fbq && Array.isArray(orderDetails.items)) {
                window.fbq('track', 'Purchase', {
                    value: orderDetails.total,
                    currency: 'EGP',
                    content_ids: orderDetails.items.map(item => item.id),
                    content_type: 'product',
                    num_items: orderDetails.items.length
                }, { eventID: orderId }); 
            }

            // 3. Send Notifications (Email is sent client-side via EmailJS; Telegram is triggered securely via backend Cloud Function)
            sendOrderConfirmationEmail(orderDetails, orderId);

            setOrderConfirmation({ ...orderData, id: orderId });
            setIsCheckoutModalOpen(false);
        } catch (error) {
            console.error("Error placing order: ", error);
            showNotification("There was an error placing your order. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const placeOrderRef = useRef(placeOrder);
    useEffect(() => {
        placeOrderRef.current = placeOrder;
    });

    // ==========================================
    // Feature 12: UI & UX Enhancements (Data Fetching & Global Settings)
    // Includes features like mobile pull-to-refresh, image lightboxes, toast notifications, and side panels.
    // ==========================================
    // --- PAYMOB PAYMENT CALLBACK HANDLER ---
    const handlePaymobCallbackParams = useCallback(async (searchString) => {
        if (!searchString) return;
        const normalizedSearch = '?' + searchString.replace(/^\?/, '').replace(/\?/g, '&');
        const params = new URLSearchParams(normalizedSearch);
        const isPaymobCallback =
            params.has('paymob_callback') ||
            (params.has('success') && (params.has('order') || params.has('id') || params.has('amount_cents') || params.has('intention')));
        if (!isPaymobCallback) return;

        // If loaded inside the checkout iframe after 3DS redirect, forward to parent window
        if (window.self !== window.top) {
            window.parent.postMessage({ type: 'UKIYO_PAYMOB_CALLBACK', search: normalizedSearch }, window.location.origin);
            return;
        }

        // Clean query parameters from URL so refreshing does not re-trigger
        window.history.replaceState({}, document.title, window.location.pathname);

        const success = params.get('success') === 'true';
        const pending = params.get('pending') === 'true';
        const txId = params.get('id');
        const paymobOrderId = params.get('order');

        const isValidHmac = await verifyPaymobHmac(params);
        if (!isValidHmac) {
            showNotification("Payment verification failed. Please contact support.");
            return;
        }

        if (success && !pending) {
            const rawPendingOrder = localStorage.getItem('ukiyo-pending-paymob-order');
            if (rawPendingOrder) {
                localStorage.removeItem('ukiyo-pending-paymob-order');
                try {
                    const pendingOrder = JSON.parse(rawPendingOrder);
                    await placeOrderRef.current({
                        ...pendingOrder,
                        paymentMethod: 'creditCard',
                        paymentStatus: 'Paid',
                        paymentDetails: {
                            gateway: 'paymob',
                            transactionId: txId,
                            paymobOrderId: paymobOrderId
                        }
                    });
                    showNotification("Payment successful! Your order has been placed.");
                } catch (err) {
                    console.error("Error finalizing Paymob order:", err);
                }
            }
        } else {
            setIsCheckoutModalOpen(false);
            showNotification("Card payment was declined or not completed. Please try again.");
        }
    }, [showNotification]);

    useEffect(() => {
        if (!authReady) return;
        if (window.location.search) {
            handlePaymobCallbackParams(window.location.search);
        }
        const onMessage = (event) => {
            if (event.origin === window.location.origin && event.data?.type === 'UKIYO_PAYMOB_CALLBACK') {
                handlePaymobCallbackParams(event.data.search);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [authReady, handlePaymobCallbackParams]);

    // --- DATA FETCHING & SUBSCRIPTIONS ---
    useEffect(() => {
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
            favicon.href = '/favicon-optimized.png';
        }
    }, []);
    
    useEffect(() => {
        let userUnsubscribe = null;
        let cartUnsubscribe = null;
        let wishlistUnsubscribe = null;

        const cleanupUserListeners = () => {
            if (userUnsubscribe) { userUnsubscribe(); userUnsubscribe = null; }
            if (cartUnsubscribe) { cartUnsubscribe(); cartUnsubscribe = null; }
            if (wishlistUnsubscribe) { wishlistUnsubscribe(); wishlistUnsubscribe = null; }
        };

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            cleanupUserListeners();
            setCurrentUser(user);
            setAuthReady(true);
            
            if (user) {
                const userRef = doc(db, "users", user.uid);
                userUnsubscribe = onSnapshot(userRef, (docSnap) => {
                    if (docSnap.exists()) setUserInfo(docSnap.data());
                });
                
                const cartRef = collection(db, `users/${user.uid}/cart`);
                cartUnsubscribe = onSnapshot(cartRef, snapshot => {
                    setCart(snapshot.docs.map(docSnap => {
                        const data = docSnap.data();
                        const price = Number(data.price) || 0;
                        const rawSale = Number(data.salePrice);
                        return { id: docSnap.id, ...data, price, salePrice: (rawSale > 0 && rawSale < price) ? rawSale : null };
                    }));
                });

                const wishlistRef = collection(db, `users/${user.uid}/wishlist`);
                wishlistUnsubscribe = onSnapshot(wishlistRef, (snapshot) => {
                    setWishlist(snapshot.docs.map(docSnap => docSnap.id));
                });

                try {
                    const adminRef = doc(db, "admins", user.uid);
                    const adminDoc = await getDoc(adminRef);
                    setIsAdmin(adminDoc.exists());
                } catch {
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
                setUserInfo(null);
                setWishlist([]);
                try {
                    const localCart = localStorage.getItem('ukiyo-guest-cart');
                    const parsed = localCart ? JSON.parse(localCart) : [];
                    setCart(parsed.map(item => {
                        const price = Number(item.price) || 0;
                        const rawSale = Number(item.salePrice);
                        return { ...item, price, salePrice: (rawSale > 0 && rawSale < price) ? rawSale : null };
                    }));
                } catch { setCart([]); }
            }
        });
        return () => {
            cleanupUserListeners();
            unsubscribe();
        };
    }, []);
    
    useEffect(() => {
        const productsRef = collection(db, "products");
        const unsubscribe = onSnapshot(productsRef, snapshot => {
            setProducts(
                snapshot.docs.map(doc => {
                    const data = doc.data();
                    const price = Math.max(0, Number(data.price) || 0);
                    const rawSale = Number(data.salePrice);
                    return { id: doc.id, ...data, price, salePrice: (rawSale > 0 && rawSale < price) ? rawSale : null };
                })
            );
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const announcementRef = doc(db, "storeSettings", "announcement");
        const unsubscribe = onSnapshot(announcementRef, (doc) => {
            if (doc.exists()) {
                setAnnouncement(doc.data());
            } else {
                setAnnouncement({ text: '', isActive: false });
            }
        });
        return () => unsubscribe();
    }, []);
    
    useEffect(() => {
        const deliveryRef = doc(db, "storeSettings", "delivery");
        const unsubscribe = onSnapshot(deliveryRef, (doc) => {
            if (doc.exists()) {
                setDeliveryCosts(doc.data());
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const bundleRef = doc(db, "storeSettings", "bundleSection");
        const unsubscribe = onSnapshot(bundleRef, (docSnap) => {
            if (docSnap.exists()) {
                setBundleSettings(prev => ({ ...prev, ...docSnap.data() }));
            }
        });
        return () => unsubscribe();
    }, []);

    const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <BrowserRouter>
            <RouteTracker />
            <Routes>
                <Route path="/" element={
                    <Layout
                        isAdmin={isAdmin}
                        cartItemCount={cartItemCount}
                        setShowCart={setShowCart}
                        onMenuClick={() => setIsMenuPanelOpen(true)}
                        onSearchClick={() => setIsSearchOverlayOpen(true)}
                        currentUser={currentUser}
                        userInfo={userInfo}
                        announcement={announcement}
                        allProducts={sortedProducts}
                        wishlist={wishlist}
                        onToggleWishlist={handleToggleWishlist}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        bundleSettings={bundleSettings}
                    />
                }>
                    <Route index element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            {/* --- UPDATED: Pass allProducts to LandingPage --- */}
                            <LandingPage allProducts={sortedProducts} />
                        </React.Suspense> 
                    } />
                    <Route path="/products" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <ShopPage allProducts={sortedProducts} wishlist={wishlist} onToggleWishlist={handleToggleWishlist} searchQuery={searchQuery} /> 
                        </React.Suspense>
                    } />
                    <Route path="/products/category/:categoryValue" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <ShopPage allProducts={sortedProducts} wishlist={wishlist} onToggleWishlist={handleToggleWishlist} searchQuery={searchQuery} /> 
                        </React.Suspense>
                    } />
                    <Route path="/on-sale" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <OnSalePage allProducts={sortedProducts} wishlist={wishlist} onToggleWishlist={handleToggleWishlist} /> 
                        </React.Suspense>
                    } />
                    <Route path="/bundles" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <BundlesPage allProducts={sortedProducts} onAddBundleToCart={handleAddBundleToCart} showNotification={showNotification} /> 
                        </React.Suspense>
                    } />
                    <Route path="product/:productId" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <ProductPage onAddToCart={handleAddToCart} onImageClick={(images, index) => setLightboxData({ images, index })} currentUser={currentUser} userInfo={userInfo} showNotification={showNotification} wishlist={wishlist} onToggleWishlist={handleToggleWishlist} /> 
                        </React.Suspense>
                    } />
                    <Route path="wishlist" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <WishlistPage allProducts={sortedProducts} wishlist={wishlist} onToggleWishlist={handleToggleWishlist} /> 
                        </React.Suspense>
                    } />
                    <Route path="profile" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <ProfilePage userInfo={userInfo} onUpdate={handleUpdateUserInfo} onPasswordReset={handlePasswordReset} onEmailUpdate={handleEmailUpdate} showNotification={showNotification} /> 
                        </React.Suspense>
                    } />
                    <Route path="orders" element={
                        <React.Suspense fallback={<PageLoader />}>
                            <OrdersPage />
                        </React.Suspense>
                    } />
                    <Route path="return-policy" element={
                        <React.Suspense fallback={<PageLoader />}>
                            <ReturnPolicyPage />
                        </React.Suspense>
                    } />
                    {isAdmin && <Route path="admin" element={ 
                        <React.Suspense fallback={<PageLoader />}>
                            <AdminPage products={sortedProducts} onAddProductClick={() => setIsAddProductModalOpen(true)} onEditProductClick={(product) => setEditingProduct(product)} onDeleteProduct={confirmDeleteProduct} showNotification={showNotification} currentUser={currentUser} /> 
                        </React.Suspense>
                    } />}
                </Route>
            </Routes>
            <Notification message={notification.message} show={notification.show} />
            {/* Removed getStylingAdvice prop here */}
            <Cart showCart={showCart} setShowCart={setShowCart} cart={cart} updateCartItem={handleUpdateCartItem} removeFromCart={handleRemoveFromCart} openCheckout={handleOpenCheckout} />
            <AuthModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} onLogin={handleEmailLogin} onRegister={handleEmailRegister} onGoogleSignIn={handleGoogleSignIn} onSendLink={handleSendSignInLink} />
            <CheckoutModal isOpen={isCheckoutModalOpen} onClose={() => setIsCheckoutModalOpen(false)} cart={cart} userInfo={userInfo} onApplyCoupon={handleApplyCoupon} couponError={couponError} discount={couponDiscount} deliveryCosts={deliveryCosts} isFreeDelivery={isFreeDelivery} placeOrder={placeOrder} currentUser={currentUser} onLoginClick={() => setIsLoginModalOpen(true)} />
            <MenuPanel isOpen={isMenuPanelOpen} onClose={() => setIsMenuPanelOpen(false)} currentUser={currentUser} userInfo={userInfo} isAdmin={isAdmin} onLoginClick={() => setIsLoginModalOpen(true)} onLogoutClick={handleLogout} allProducts={sortedProducts} bundleSettings={bundleSettings} />
            <SearchOverlay isOpen={isSearchOverlayOpen} onClose={() => setIsSearchOverlayOpen(false)} allProducts={products} onLogSearch={handleLogSearch} />
            <AnimatePresence> {lightboxData && <ImageLightbox data={lightboxData} onClose={() => setLightboxData(null)} />} </AnimatePresence>
            {orderConfirmation && <OrderConfirmationModal isOpen={!!orderConfirmation} data={orderConfirmation} onClose={() => setOrderConfirmation(null)} /> }
            <AddProductModal isOpen={isAddProductModalOpen} onClose={() => setIsAddProductModalOpen(false)} onAddProduct={handleAddProduct} />
            <EditProductModal isOpen={!!editingProduct} onClose={() => setEditingProduct(null)} product={editingProduct} onUpdateProduct={handleUpdateProduct} />
            <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} onConfirm={() => confirmDeleteProduct([productToDelete])} title="Delete Product" message="Are you sure you want to delete this product? This action cannot be undone." />
            <UploadingModal isOpen={isUploading} message="Processing..." />
            <TermsBanner
                onAcceptTerms={() => showNotification("Thank you for accepting our Terms & Conditions.")}
                onDeclineTerms={() => showNotification("You can continue browsing. Accepting our Terms & Conditions is required at checkout.")}
            />
        </BrowserRouter>
    );
}

// --- NOTIFICATION HELPER FUNCTIONS ---

// ==========================================
// Feature 4 (Continued): Order Processing & Tracking
// Helper functions for sending external notifications (Email and Telegram).
// ==========================================
const sendOrderConfirmationEmail = (orderDetails, orderId) => {
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
        console.log("EmailJS credentials not configured, skipping email.");
        return;
    }

    let invoiceHtml = `
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
            <thead>
                <tr style="background-color: #fbf8f3; border-bottom: 2px solid #b08d57;">
                    <th style="text-align: left; padding: 10px 12px; font-size: 12px; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Item</th>
                    <th style="text-align: center; padding: 10px 12px; font-size: 12px; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Qty</th>
                    <th style="text-align: right; padding: 10px 12px; font-size: 12px; color: #333; text-transform: uppercase; letter-spacing: 0.5px;">Price</th>
                </tr>
            </thead>
            <tbody>`;

    orderDetails.items.forEach(item => {
        const price = item.salePrice || item.price;
        invoiceHtml += `
            <tr style="border-bottom: 1px solid #eeeeee;">
                <td style="padding: 12px; color: #333333; font-size: 14px; font-weight: 500;">${item.name}</td>
                <td style="text-align: center; padding: 12px; color: #666666; font-size: 14px;">${item.quantity}</td>
                <td style="text-align: right; padding: 12px; color: #333333; font-size: 14px; font-weight: 600;">EGP ${(price * item.quantity).toFixed(2)}</td>
            </tr>`;
    });

    invoiceHtml += `</tbody></table>
        <div style="margin-top: 16px; padding-top: 12px; border-top: 1px dashed #dddddd; text-align: right;">
            <p style="margin: 4px 0; color: #666666; font-size: 13px;">Subtotal: <strong style="color: #333333;">EGP ${orderDetails.subtotal.toFixed(2)}</strong></p>
            <p style="margin: 4px 0; color: #666666; font-size: 13px;">Shipping: <strong style="color: #333333;">EGP ${orderDetails.shipping.toFixed(2)}</strong></p>`;
    
    if (orderDetails.paymentFee > 0) {
        let methodLabel = orderDetails.paymentMethod === 'cod' ? 'Cash on Delivery' : 
                          orderDetails.paymentMethod === 'creditCard' ? 'Credit Card' : orderDetails.paymentMethod;
        invoiceHtml += `<p style="margin: 4px 0; color: #666666; font-size: 13px;">${methodLabel} Fee: <strong style="color: #333333;">EGP ${orderDetails.paymentFee.toFixed(2)}</strong></p>`;
    }
    
    if (orderDetails.discount > 0) {
        invoiceHtml += `<p style="margin: 4px 0; color: #16a34a; font-size: 13px;">Discount: <strong>-EGP ${orderDetails.discount.toFixed(2)}</strong></p>`;
    }
    
    invoiceHtml += `<h3 style="margin: 12px 0 0 0; color: #b08d57; font-size: 18px; font-weight: bold;">Total: EGP ${orderDetails.total.toFixed(2)}</h3></div>`;

    const recipientEmail = orderDetails.customer.email;

    const templateParams = {
        to_email: recipientEmail,
        from_name: "Ukiyo Homes", 
        customer_name: orderDetails.customer.name,
        order_id: orderId, 
        order_items: invoiceHtml, 
        order_total: `EGP ${orderDetails.total.toFixed(2)}`,
        customer_address: orderDetails.customer.address,
        customer_city: orderDetails.customer.city
    };

    emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY)
        .then((response) => {
           console.log('SUCCESS!', response.status, response.text);
        }, (err) => {
           console.log('FAILED...', err);
        });
};